import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import {
  NetworkData,
  LayoutType,
  DEFAULT_LAYOUT,
  DEFAULT_FILTER_SETTINGS,
  DEFAULT_NODE_TYPE_FILTERS,
  NodeType,
  NodeTypeFilterSettings,
  Node,
} from '../types/cytoscape';

import {
  networkConditions,
  NetworkSettingsProps,
} from '@/features/connect-dots/utils/networkConditions';
import { useNetworkLayout } from './useNetworkLayout';
import { Core } from 'cytoscape';
import { NodeTooltipProps } from '@/features/connect-dots/components/tooltips/NodeTooltip';
import { useNodeSelection } from '@/features/connect-dots/hooks/useNodeSelection';
import { useNetworkData } from '@/features/connect-dots/hooks/useNetworkData'; // ★ 変更: useGetNetworkData として使用

import { useAtom } from 'jotai';
import { networkAtom } from '@/features/connect-dots/atoms/networkAtom';

export const useNetworkState = (
  initialData: NetworkData = { nodes: [], edges: [] }
) => {
  // データ読み込み状態のフラグ
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);

  // 元のネットワークデータ（未フィルター）
  const [networkData, setNetworkData] = useState<NetworkData>(initialData);

  //
  // const [childDocsForTagCloud, setChildDocsForTagCloud] = useState<topicTag[]>(
  //   []
  // );

  // Cytoscapeインスタンスの参照
  const cytoscapeCoreRefObject = useRef<Core | null>(null);

  // フィルター設定の状態
  const [minNodeWeight, setMinNodeWeight] = useState<number>(
    DEFAULT_FILTER_SETTINGS.minNodeWeight
  );
  const [minEdgeWeight, setMinEdgeWeight] = useState<number>(
    DEFAULT_FILTER_SETTINGS.minEdgeWeight
  );
  const [minNodeFrequency, setMinNodeFrequency] = useState<number>(
    DEFAULT_FILTER_SETTINGS.minNodeFrequency
  );
  const [hideTagsWithoutDocuments, setHideTagsWithoutDocuments] =
    useState<boolean>(DEFAULT_FILTER_SETTINGS.hideTagsWithoutDocuments);
  const [showOnlySocialIssues, setShowOnlySocialIssues] = useState<boolean>(
    DEFAULT_FILTER_SETTINGS.showOnlySocialIssues
  );

  // ノードタイプ選択条件の追加
  const [nodeTypeFilters, setNodeTypeFilters] =
    useState<NodeTypeFilterSettings>(DEFAULT_NODE_TYPE_FILTERS);

  // UI関連の状態
  const [nodeSpacingFactor, setNodeSpacingFactor] = useState<number>(10);
  const [isInfoPanelVisible, setIsInfoPanelVisible] = useState<boolean>(false);
  const [currentLayout, setCurrentLayout] =
    useState<LayoutType>(DEFAULT_LAYOUT);

  // ツールチップ状態
  const [tooltipState, setTooltipState] = useState<NodeTooltipProps>({
    visible: false,
    content: null,
    position: { x: 0, y: 0 },
  });

  // ノード選択状態
  const { selectedNodeSingulars, addSelectedNodeSingular, selectedNodeIds } =
    useNodeSelection(cytoscapeCoreRefObject);

  // レイアウト関連のフック
  const { changeLayout } = useNetworkLayout(
    cytoscapeCoreRefObject,
    nodeSpacingFactor,
    null
  );

  // フィルター設定をオブジェクトとしてまとめる
  const conditionSettings: NetworkSettingsProps = useMemo(
    () => ({
      filters: [],
      minEdgeWeight,
      minNodeWeight,
      minNodeFrequency,
      hideTagsWithoutDocuments,
      showOnlySocialIssues,
      nodeTypeFilters,
    }),
    [
      minEdgeWeight,
      minNodeWeight,
      minNodeFrequency,
      hideTagsWithoutDocuments,
      showOnlySocialIssues,
      nodeTypeFilters,
    ]
  );

  const prevNetworkRef = useRef<NetworkData | null>(null);
  // ★ カスタムフックとして直接呼び出し
  const { error: networkError } = useNetworkData();

  const [network] = useAtom(networkAtom);

  // networkDataの更新時のみset
  useEffect(() => {
    if (JSON.stringify(prevNetworkRef.current) !== JSON.stringify(network)) {
      prevNetworkRef.current = network;

      if (network) {
        setNetworkData(network);
      } else {
        setNetworkData(initialData);
      }
    }
  }, [network]);

  function deepEqual<T>(a: T, b: T): boolean {
    if (a === b) return true;
    if (typeof a !== typeof b) return false;
    if (typeof a !== 'object' || a === null || b === null) return false;

    // 配列の場合
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!deepEqual(a[i], b[i])) return false;
      }
      return true;
    }

    // オブジェクトの場合
    if (!Array.isArray(a) && !Array.isArray(b)) {
      const keysA = Object.keys(a as Record<string, unknown>);
      const keysB = Object.keys(b as Record<string, unknown>);
      if (keysA.length !== keysB.length) return false;
      for (const key of keysA) {
        if (!keysB.includes(key)) return false;
        if (
          !deepEqual(
            (a as Record<string, unknown>)[key],
            (b as Record<string, unknown>)[key]
          )
        )
          return false;
      }
      return true;
    }

    // 型が一致しない場合
    return false;
  }

  function memorizedNetworkConditions() {
    let lastArgs: {
      networkData: NetworkData;
      conditionSettings: NetworkSettingsProps;
    } | null = null;
    let lastResult: ReturnType<typeof networkConditions> | null = null;
    return (
      networkData: NetworkData,
      conditionSettings: NetworkSettingsProps
    ) => {
      if (
        lastArgs &&
        deepEqual(lastArgs.networkData, networkData) &&
        deepEqual(lastArgs.conditionSettings, conditionSettings)
      ) {
        return lastResult;
      }
      const result = networkConditions(networkData, conditionSettings);
      lastArgs = { networkData, conditionSettings };
      lastResult = result;
      return result;
    };
  }

  const getFilteredData = memorizedNetworkConditions();

  // conditionSettings で、networkDataのフィルタリングを行う
  // （※ここでいうフィルタリングは、UI上のフィルタリングではない）
  const filteredData = useMemo(
    () => getFilteredData(networkData, conditionSettings),
    [networkData, conditionSettings]
  );

  // filteredDataを明示的に監視して状態を更新
  const [isFilteringComplete, setIsFilteringComplete] = useState(false);
  useEffect(() => {
    setIsFilteringComplete(
      !!filteredData?.nodes && filteredData.nodes.length > 0
    );
  }, [filteredData]);

  // isDataLoadedをfilterDataの状態と連動させる
  useEffect(() => {
    if (networkData?.nodes?.length > 0 && isFilteringComplete) {
      setIsDataLoaded(true);
    } else {
      setIsDataLoaded(false); // データが空の場合はfalseにリセット
    }
  }, [networkData, isFilteringComplete]);

  // 単一ノードタイプのフィルター更新関数
  const updateNodeTypeFilter = useCallback(
    (nodeType: NodeType, isVisible: boolean) => {
      setNodeTypeFilters((prev) => ({
        ...prev,
        [nodeType]: isVisible,
      }));
    },
    []
  );

  // ツールチップ関連アクション
  const showTooltip = useCallback(
    (content: Node, position: { x: number; y: number }) => {
      setTooltipState({
        visible: true,
        content,
        position,
      });
    },
    []
  );

  const hideTooltip = useCallback(() => {
    setTooltipState((prev: NodeTooltipProps) => ({
      ...prev,
      visible: false,
    }));
  }, []);

  return {
    // 読み込み状態
    isDataLoaded,
    // setIsDataLoaded,

    // 元データ
    // networkData,

    // フィルタリング後のデータ
    filteredData,
    // childDocsForTagCloud,
    networkError,

    // フィルター設定と更新関数
    minNodeWeight,
    setMinNodeWeight,
    minEdgeWeight,
    setMinEdgeWeight,
    minNodeFrequency,
    setMinNodeFrequency,
    hideTagsWithoutDocuments,
    setHideTagsWithoutDocuments,
    showOnlySocialIssues,
    setShowOnlySocialIssues,
    nodeTypeFilters,
    setNodeTypeFilters,
    updateNodeTypeFilter,

    // UI関連
    nodeSpacingFactor,
    setNodeSpacingFactor,
    isInfoPanelVisible,
    setIsInfoPanelVisible,

    // レイアウト関連
    currentLayout,
    setCurrentLayout,
    changeLayout,

    // Cytoscape関連
    cytoscapeCoreRefObject,
    selectedNodeSingulars,
    addSelectedNodeSingular,
    selectedNodeIds,

    // ツールチップ関連
    tooltipState,
    showTooltip,
    hideTooltip,

    // フィルタリング完了状態
    isFilteringComplete,
  };
};
